import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from "@mui/icons-material";
import { YouTubeSearchResults } from "./YouTubeSearchResults";
import { YouTubeChannelDetail } from "./YouTubeChannelDetail";
import {
  ChannelConfirmationModal,
  ChannelFormValues,
} from "./ChannelConfirmationModal";
import { LoadingOverlay } from "../common/LoadingOverlay";
import { useYouTubeSearch } from "../../hooks/useYouTubeSearch";
import { addOutreachItem } from "../../services/outreach/firestore/crud";
import type {
  YouTubeChannelSearchDetails,
  YouTubeSortOrder,
} from "../../types/outreach";
import { OutreachCategory } from "../../types/outreach";
import {
  formatLegacySocialHandles,
  normalizeSocialLinkInput,
} from "../../utils/socialLinks";

interface YouTubeSearchModalProps {
  open: boolean;
  onClose: () => void;
  reactionChannels: Array<{ channelUrl?: string }>;
  playlistingChannels: Array<{ channelUrl?: string }>;
  onChannelAdded: () => void;
  activeCategory: OutreachCategory;
}

const DEFAULT_SEARCH_QUERIES = [
  "Reaction channel",
  "Music reaction",
  "Album reaction",
  "First time hearing",
  "Music playlist",
  "Curated playlist",
];

export const YouTubeSearchModal: React.FC<YouTubeSearchModalProps> = ({
  open,
  onClose,
  reactionChannels,
  playlistingChannels,
  onChannelAdded,
  activeCategory,
}) => {
  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    selectedChannel,
    loadingState,
    loading,
    detailsLoading,
    error,
    totalResults,
    performSearch,
    fetchChannelDetails,
    resetSearch,
    currentPage,
    pageToken,
    prevPageToken,
    nextPage,
    prevPage,
    sortOrder,
    setSortOrder,
    resultsPerPage,
    setResultsPerPage,
    canRetry,
    retryCount,
    performRetry,
  } = useYouTubeSearch();

  const [selectedChannelId, setSelectedChannelId] = useState<
    string | undefined
  >(undefined);
  const [notification, setNotification] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [pendingCategory, setPendingCategory] =
    useState<OutreachCategory | null>(null);
  const [pendingChannel, setPendingChannel] =
    useState<YouTubeChannelSearchDetails | null>(null);
  const [initialFormValues, setInitialFormValues] =
    useState<ChannelFormValues | null>(null);
  const [savingCategory, setSavingCategory] = useState<OutreachCategory | null>(
    null,
  );
  const [displayError, setDisplayError] = useState<string | null>(null);

  // Update displayError when error state changes
  useEffect(() => {
    if (error) {
      setDisplayError(error);
    }
  }, [error]);

  useEffect(() => {
    if (!open) {
      resetSearch();
      setSelectedChannelId(undefined);
      setConfirmationOpen(false);
      setPendingCategory(null);
      setPendingChannel(null);
      setInitialFormValues(null);
      setDisplayError(null);
    }
  }, [open, resetSearch]);

  const handleSelectChannel = (channelId: string) => {
    setSelectedChannelId(channelId);
    fetchChannelDetails(channelId).catch((err) => {
      console.error("Failed to fetch channel details", err);
    });
  };

  const handleSearch = () => {
    void performSearch();
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleQuickSearch = (query: string) => {
    setSearchQuery(query);
    void performSearch(query);
  };

  const handleSortChange = (event: SelectChangeEvent<YouTubeSortOrder>) => {
    const newSortOrder = event.target.value as YouTubeSortOrder;
    setSortOrder(newSortOrder);
    // Trigger new search with updated sort order
    // setSortOrder already resets pagination to page 1
    if (searchQuery.trim()) {
      void performSearch();
    }
  };

  const handleResultsPerPageChange = (event: SelectChangeEvent<number>) => {
    const newResultsPerPage = Number(event.target.value);
    setResultsPerPage(newResultsPerPage);
    // setResultsPerPage already resets pagination to page 1
    if (searchQuery.trim()) {
      void performSearch();
    }
  };

  const checkChannelExists = useCallback(
    (channelId: string, category: OutreachCategory): boolean => {
      const channels =
        category === OutreachCategory.REACTION_CHANNELS
          ? reactionChannels
          : playlistingChannels;

      return channels.some((channel) => {
        const url = channel.channelUrl ?? "";
        return url.includes(channelId);
      });
    },
    [playlistingChannels, reactionChannels],
  );

  const getExistingChannelStatus = useCallback(
    (channelId: string) => ({
      reaction: checkChannelExists(
        channelId,
        OutreachCategory.REACTION_CHANNELS,
      ),
      playlisting: checkChannelExists(
        channelId,
        OutreachCategory.PLAYLISTING_CHANNELS,
      ),
    }),
    [checkChannelExists],
  );

  const buildChannelPayload = (
    channel: YouTubeChannelSearchDetails,
  ): ChannelFormValues => {
    const youtubeHandleSource = channel.customUrl
      ? channel.customUrl
      : `channel/${channel.channelId}`;
    const youtubeSocialLink = normalizeSocialLinkInput(
      "youtube",
      youtubeHandleSource,
    );
    const socialLinks = [youtubeSocialLink];
    const socialHandles = channel.customUrl
      ? channel.customUrl
      : formatLegacySocialHandles(socialLinks);

    return {
      channelName: channel.title,
      channelUrl: `https://www.youtube.com/channel/${channel.channelId}`,
      subscriberCount: channel.subscriberCount,
      email: channel.email ?? "",
      socialHandles,
      socialLinks,
      viewCount: channel.viewCount.toString(),
      videoCount: channel.videoCount.toString(),
      likes: "0",
      genre: "Any",
      lastUpdated: new Date().toISOString(),
    };
  };

  const openConfirmationModal = (
    channel: YouTubeChannelSearchDetails,
    category: OutreachCategory,
  ) => {
    setPendingChannel(channel);
    setPendingCategory(category);
    setInitialFormValues(buildChannelPayload(channel));
    setConfirmationOpen(true);
  };

  const handleAddToReactionChannels = (
    channel: YouTubeChannelSearchDetails,
  ) => {
    if (
      checkChannelExists(channel.channelId, OutreachCategory.REACTION_CHANNELS)
    ) {
      setNotification({
        message: "Channel already exists in Reaction Channels.",
        type: "error",
      });
      return;
    }

    openConfirmationModal(channel, OutreachCategory.REACTION_CHANNELS);
  };

  const handleAddToPlaylistingChannels = (
    channel: YouTubeChannelSearchDetails,
  ) => {
    if (
      checkChannelExists(
        channel.channelId,
        OutreachCategory.PLAYLISTING_CHANNELS,
      )
    ) {
      setNotification({
        message: "Channel already exists in Playlisting Channels.",
        type: "error",
      });
      return;
    }

    openConfirmationModal(channel, OutreachCategory.PLAYLISTING_CHANNELS);
  };

  const handleCloseConfirmation = () => {
    setConfirmationOpen(false);
    setPendingCategory(null);
    setPendingChannel(null);
    setInitialFormValues(null);
  };

  const handleConfirmAdd = async (values: ChannelFormValues) => {
    if (!pendingCategory) {
      return;
    }

    setSavingCategory(pendingCategory);

    try {
      await addOutreachItem(pendingCategory, values);
      setNotification({
        message:
          pendingCategory === OutreachCategory.REACTION_CHANNELS
            ? "Channel added to Reaction Channels successfully!"
            : "Channel added to Playlisting Channels successfully!",
        type: "success",
      });
      onChannelAdded();
      handleCloseConfirmation();
    } catch (err) {
      console.error("Error adding channel:", err);
      setNotification({
        message: "Failed to add channel. Please try again.",
        type: "error",
      });
    } finally {
      setSavingCategory(null);
    }
  };

  const addingToReaction =
    savingCategory === OutreachCategory.REACTION_CHANNELS;
  const addingToPlaylisting =
    savingCategory === OutreachCategory.PLAYLISTING_CHANNELS;
  const preferredCategory =
    activeCategory === OutreachCategory.PLAYLISTING_CHANNELS
      ? OutreachCategory.PLAYLISTING_CHANNELS
      : OutreachCategory.REACTION_CHANNELS;

  const handleCloseNotification = () => {
    setNotification(null);
  };

  const handleDismissError = () => {
    setDisplayError(null);
  };

  const handleRetrySearch = () => {
    setDisplayError(null);
    handleSearch();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: "90vw",
          height: "85vh",
          maxWidth: "none",
          maxHeight: "none",
          display: "flex",
          flexDirection: "column",
        },
      }}
      aria-labelledby="youtube-search-dialog-title"
    >
      <DialogTitle id="youtube-search-dialog-title" sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography variant="h6">Search YouTube Channels</Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <LoadingOverlay
        isLoading={loadingState.isLoading}
        operation={loadingState.operation}
        page={loadingState.page}
      />

      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          minHeight: 0,
          overflow: "hidden",
          position: "relative",
        }}
        aria-busy={loadingState.isLoading}
      >
        <Grid container sx={{ flexGrow: 1, minHeight: 0, height: "100%" }}>
          {/* Left Column: Search Input + Results */}
          <Grid
            item
            xs={12}
            md={7}
            sx={{
              display: "flex",
              flexDirection: "column",
              borderRight: { xs: "none", md: "1px solid rgba(0, 0, 0, 0.12)" },
              minHeight: 0,
              height: "100%",
              overflow: "hidden",
            }}
          >
            {/* Search Input Section */}
            <Box sx={{ px: 3, pt: 2, pb: 2, flexShrink: 0 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search for YouTube channels..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        color="primary"
                        onClick={handleSearch}
                        disabled={loading || !searchQuery.trim()}
                        aria-label="search"
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Sort Dropdown and Results Per Page Selector */}

              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "flex-start",
                  gap: 2,
                }}
              >
                <FormControl
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <InputLabel id="sort-label">Sort by</InputLabel>
                  <Select
                    labelId="sort-label"
                    value={sortOrder}
                    onChange={handleSortChange}
                    label="Sort by"
                    aria-label="Sort search results"
                    disabled={loadingState.isLoading}
                  >
                    <MenuItem value="relevance">Relevance</MenuItem>
                    <MenuItem value="viewCount">View Count (High-Low)</MenuItem>
                    <MenuItem value="title">Name (A-Z)</MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: 150 }}
                >
                  <InputLabel id="results-per-page-label">Results</InputLabel>
                  <Select
                    labelId="results-per-page-label"
                    value={resultsPerPage}
                    onChange={handleResultsPerPageChange}
                    label="Results"
                    aria-label="Results per page"
                    disabled={loadingState.isLoading}
                  >
                    <MenuItem value={10}>10 per page</MenuItem>
                    <MenuItem value={20}>20 per page</MenuItem>
                    <MenuItem value={50}>50 per page</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {displayError && (
                <Alert
                  severity="error"
                  sx={{ mt: 2 }}
                  onClose={handleDismissError}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleRetrySearch}
                      disabled={loading}
                      sx={{ ml: 1 }}
                    >
                      Retry
                    </Button>
                  }
                >
                  {displayError}
                </Alert>
              )}
            </Box>

            {/* Results Table Section */}
            <Box
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <YouTubeSearchResults
                results={searchResults}
                loading={loading}
                totalResults={totalResults}
                onSelectChannel={handleSelectChannel}
                selectedChannelId={selectedChannelId}
                getExistingChannelStatus={getExistingChannelStatus}
              />
            </Box>
          </Grid>

          {/* Right Column: Channel Details */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 3,
                flexGrow: 1,
                minHeight: 0,
                overflowY: "auto",
              }}
            >
              <YouTubeChannelDetail
                channel={selectedChannel}
                loading={detailsLoading}
                onAddToReactionChannels={handleAddToReactionChannels}
                onAddToPlaylistingChannels={handleAddToPlaylistingChannels}
                reactionChannelExists={
                  selectedChannel
                    ? checkChannelExists(
                        selectedChannel.channelId,
                        OutreachCategory.REACTION_CHANNELS,
                      )
                    : false
                }
                playlistingChannelExists={
                  selectedChannel
                    ? checkChannelExists(
                        selectedChannel.channelId,
                        OutreachCategory.PLAYLISTING_CHANNELS,
                      )
                    : false
                }
                addingToReaction={addingToReaction}
                addingToPlaylisting={addingToPlaylisting}
                preferredCategory={preferredCategory}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Loading Overlay for Pagination */}
        <Backdrop
          sx={{
            position: "absolute",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(2px)",
            zIndex: (theme) => theme.zIndex.modal - 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
          }}
          open={loading && searchResults.length > 0}
          aria-busy={loading && searchResults.length > 0}
          aria-label="Loading search results"
        >
          <CircularProgress aria-hidden="true" />
          <Typography
            variant="body1"
            color="textPrimary"
            sx={{ fontWeight: 600 }}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            Loading results...
          </Typography>
        </Backdrop>
      </DialogContent>

      {/* Pagination Controls */}
      {searchResults.length > 0 && (
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
            borderTop: "1px solid rgba(0, 0, 0, 0.12)",
            backgroundColor: "background.paper",
            flexShrink: 0,
          }}
          role="navigation"
          aria-label="Search results pagination"
        >
          <Button
            disabled={currentPage === 1 || loadingState.isLoading}
            onClick={prevPage}
            startIcon={<NavigateBeforeIcon />}
            aria-label={`Go to previous page. Currently on page ${currentPage}`}
            aria-disabled={currentPage === 1 || loadingState.isLoading}
            variant="outlined"
          >
            Previous
          </Button>

          <Typography
            variant="body2"
            sx={{
              minWidth: { xs: "100px", sm: "120px" },
              textAlign: "center",
              fontWeight: 500,
            }}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            Page {currentPage}
          </Typography>

          <Button
            disabled={!pageToken || loadingState.isLoading}
            onClick={nextPage}
            endIcon={<NavigateNextIcon />}
            aria-label={`Go to next page. Currently on page ${currentPage}`}
            aria-disabled={!pageToken || loadingState.isLoading}
            variant="outlined"
          >
            Next
          </Button>
        </Box>
      )}

      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {notification ? (
          <Alert
            onClose={handleCloseNotification}
            severity={notification.type}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        ) : undefined}
      </Snackbar>
      <ChannelConfirmationModal
        open={confirmationOpen}
        category={pendingCategory}
        channelDetails={pendingChannel}
        initialValues={initialFormValues}
        loading={Boolean(savingCategory)}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmAdd}
      />
    </Dialog>
  );
};

export default YouTubeSearchModal;
